import type { CreateTaskNextSteps } from '@frontend/api-client';
import { calculateDefaultRecurrenceBuffer } from '@frontend/api-client';
import type {
  DraftTaskDetail,
  LocalFile,
  RecurrenceInterval,
  TaskInitialQuestion,
  TaskInitialQuestionAnswer,
  TaskInitialQuestionInput,
  ThreadReferenceForTask,
} from '@frontend/api-types';
import { generateUUID, isArray } from '@frontend/duck-tape';

export type TaskCreationOptions = {
  draftTask?: DraftTaskDetail;
  initialData?: Partial<CreateTaskData>;
  initiatedFromCaptiveThread?: boolean;
  isRecurrence?: boolean;
  onCreateCallback?: () => void;
  stepsToSkip?: SkippableCreateTaskStep[];
};

export type MultiSelectAnswer = { id: string; value: string };

export type Answer = {
  answer: MultiSelectAnswer[] | string;
  choices: MultiSelectAnswer[];
  question: TaskInitialQuestionInput['question'];
};

export const defaultTaskData = {
  attachments: [],
  buffer: calculateDefaultRecurrenceBuffer('month', 1),
  calendarRecurrenceId: undefined,
  calendarRecurrenceTaskSuggestionIdx: undefined,
  description: '',
  exampleId: undefined,
  exampleType: undefined,
  interval: 'month' as RecurrenceInterval,
  intervalCount: 1,
  letterRecommendationId: undefined,
  letterRecommendationTaskSuggestionIdx: undefined,
  name: '',
  nextStartDate: undefined,
  nextSteps: undefined,
  researchThreadId: undefined,
  researchThreadReferenceData: undefined,
  sharedWithClientIds: [],
};

export type TaskCreationState = {
  currentStep: CreateTaskStep;
  isRecurrence: boolean;
  steps: CreateTaskStep[];
  taskData: CreateTaskData;
  uniqueKey: UUID;
};

// This 'init' step is meant to prevent unintentionally running any logic in the first step of the
// flow (description, currently) when the user hasn't summoned the modal but the component is still mounted.
// This could happen when we reset the "currentStep" back to the first step of the flow.
export const allSteps = [
  'init',
  'description',
  'quota',
  'recurrenceSettings',
  'nextSteps',
  'initialQuestions',
] as const;

export type CreateTaskStep = (typeof allSteps)[number];

// we rely on nextSteps to buffer the initial questions step
export type SkippableCreateTaskStep = Exclude<CreateTaskStep, 'nextSteps'>;

export const initialTaskCreationState: TaskCreationState = {
  currentStep: allSteps[0],
  isRecurrence: false,
  steps: [...allSteps],
  taskData: { ...defaultTaskData },
  uniqueKey: generateUUID(),
};

export type CreateTaskData = {
  attachments: LocalFile[];
  buffer: number;
  calendarRecurrenceId: MaybeUndefined<string>;
  calendarRecurrenceTaskSuggestionIdx: MaybeUndefined<number>;
  description: string;
  exampleId: MaybeUndefined<string>;
  exampleType: MaybeUndefined<string>;
  interval: RecurrenceInterval;
  intervalCount: number;
  letterRecommendationId: MaybeUndefined<string>;
  letterRecommendationTaskSuggestionIdx: MaybeUndefined<number>;
  name: string;
  nextStartDate: MaybeUndefined<DateISOString>;
  nextSteps: MaybeUndefined<CreateTaskNextSteps>;
  researchThreadId: MaybeUndefined<string>;
  researchThreadReferenceData: MaybeUndefined<ThreadReferenceForTask>;
  sharedWithClientIds: string[];
};

export const formatAiQuestionnaireAnswersForSubmission = (answers: Answer[]): TaskInitialQuestionInput[] => {
  return answers.map((q): TaskInitialQuestionInput => {
    const answer = isArray(q.answer)
      ? // We want to fallback to the literal text "Other" if the other checkbox is checked and no textual answer was provided
        q.answer.map((a) => (a.id === 'Other' && !a.value ? 'Other' : a.value))
      : q.answer;
    return { answer, choices: q.choices.map((c) => c.id), question: q.question };
  });
};

export const stepsForRepeatingTasksOnly = ['recurrenceSettings'];

export type OnCreateTaskCallbackPayload = CreateTaskData & { isRecurrence: boolean };

export const formatDraftTaskAiQuestionnaire = (
  aiQuestionnaire: MaybeNull<{ responses: TaskInitialQuestionAnswer[] }>,
): {
  answers: Answer[];
  initialQuestions: TaskInitialQuestion[];
} => {
  if (!aiQuestionnaire) {
    return { answers: [], initialQuestions: [] };
  }

  const initialQuestions: TaskInitialQuestion[] = aiQuestionnaire.responses.map((response) => ({
    choices: response.choices,
    question: response.question,
  }));

  const answers: Answer[] = aiQuestionnaire.responses.map((response) => ({
    answer: Array.isArray(response.answer) ? response.answer.map((a) => ({ id: a, value: a })) : response.answer,
    choices: response.choices?.map((choice) => ({ id: choice, value: choice })) ?? [],
    question: response.question,
  }));

  return { answers, initialQuestions };
};
